import { useState } from "react";
import { Eye, X } from "lucide-react";
import CustomQuestionsFieldset, { type CustomQuestionField } from "../ui/CustomQuestionsFieldset";
import { validateCustomFields, type AnswerMap } from "../../utils/formFieldValidation";

interface FormPreviewProps {
  fields: CustomQuestionField[];
}

/**
 * Shows the registration questions exactly as a member will meet them.
 *
 * It renders through the same component the real form uses, so what is shown
 * here cannot drift from what people actually fill in. Answers typed in the
 * preview are discarded — nothing is saved and no event is created.
 */
export default function FormPreview({ fields }: FormPreviewProps) {
  const [open, setOpen] = useState(false);
  const [answers, setAnswers] = useState<AnswerMap>({});
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  if (!fields || fields.length === 0) return null;

  const sectionCount = new Set(
    fields.map((f) => f.section?.trim() || "Additional Information")
  ).size;

  const check = () => setErrors(validateCustomFields(fields as any, answers));

  const reset = () => {
    setAnswers({});
    setErrors({});
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:border-blue-500 hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <Eye className="h-4 w-4" />
        Preview form ({fields.length} question{fields.length === 1 ? "" : "s"}
        {sectionCount > 1 ? `, ${sectionCount} sections` : ""})
      </button>
    );
  }

  return (
    <div className="rounded-lg border border-blue-300 bg-blue-50/50 p-4">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h4 className="text-sm font-semibold text-gray-900">
            Preview — how members will see this
          </h4>
          <p className="mt-0.5 text-xs text-gray-600">
            Nothing here is saved. Try the questions, then press Check to see the
            messages someone would get.
          </p>
        </div>
        <button
          type="button"
          onClick={() => { setOpen(false); reset(); }}
          aria-label="Close preview"
          className="rounded p-1 text-gray-500 hover:bg-white hover:text-gray-700"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="rounded-md border border-gray-200 bg-white p-5">
        <CustomQuestionsFieldset
          fields={fields}
          answers={answers}
          errors={errors}
          onChange={(fieldName, value) => {
            setAnswers((prev) => ({ ...prev, [fieldName]: value }));
            setErrors((prev) => {
              const next = { ...prev };
              delete next[`custom_${fieldName}`];
              return next;
            });
          }}
          onToggleOption={(fieldName, option, checked) => {
            setAnswers((prev) => {
              const current = Array.isArray(prev[fieldName]) ? (prev[fieldName] as string[]) : [];
              return {
                ...prev,
                [fieldName]: checked
                  ? [...current, option]
                  : current.filter((o) => o !== option),
              };
            });
          }}
        />
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={check}
          className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
        >
          Check answers
        </button>
        <button
          type="button"
          onClick={reset}
          className="text-sm font-medium text-gray-600 hover:underline"
        >
          Clear
        </button>
        {Object.keys(errors).length > 0 && (
          <span className="text-sm text-red-600">
            {Object.keys(errors).length} question
            {Object.keys(errors).length === 1 ? "" : "s"} would block submission
          </span>
        )}
      </div>
    </div>
  );
}
